/**
 * TokenPurchaseModal — Buy extra AI query tokens via Stripe
 *
 * Shows available token packages, processes payment through Stripe Elements,
 * then confirms the purchase to grant queries to the user's daily budget.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { authFetch } from '../../services/apiAuth';
import './TokenPurchaseModal.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TokenPackage {
  id: string;
  queries: number;
  priceUsd: number;  // cents
  label: string;
}

interface TokenPurchaseModalProps {
  language: string;
  onClose: () => void;
  onSuccess: (queriesAdded: number) => void;
}

// ---------------------------------------------------------------------------
// Package definitions (must match backend TOKEN_PACKAGES)
// ---------------------------------------------------------------------------

const PACKAGES: TokenPackage[] = [
  { id: 'pack_small',  queries: 50,  priceUsd: 499,  label: '50 queries'  },
  { id: 'pack_medium', queries: 150, priceUsd: 999,  label: '150 queries' },
  { id: 'pack_large',  queries: 400, priceUsd: 2499, label: '400 queries' },
];

// Lazy-load Stripe only when the modal is used — avoids loading Stripe JS
// on every page (which triggers tracking-prevention warnings in Edge/Safari).
let stripePromise: ReturnType<typeof loadStripe> | null = null;
function getStripePromise() {
  if (!stripePromise) {
    stripePromise = loadStripe(
      import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder'
    );
  }
  return stripePromise;
}

const isStripeTestMode = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '').startsWith('pk_test_');

// ---------------------------------------------------------------------------
// Inner form (needs Stripe context)
// ---------------------------------------------------------------------------

const TokenPurchaseForm: React.FC<TokenPurchaseModalProps> = ({ language, onClose, onSuccess }) => {
  const { t } = useTranslation('chat');
  const stripe = useStripe();
  const elements = useElements();
  const [selectedPkg, setSelectedPkg] = useState<TokenPackage | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'select' | 'pay'>('select');

  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const handleSelectPackage = (pkg: TokenPackage) => {
    setSelectedPkg(pkg);
    setError(null);
    setStep('pay');
  };

  const handleBack = () => {
    setStep('select');
    setSelectedPkg(null);
    setError(null);
  };

  const handlePurchase = async () => {
    if (!stripe || !elements || !selectedPkg) return;
    setProcessing(true);
    setError(null);

    try {
      // 1. Create PaymentIntent on backend
      const intentRes = await authFetch('/api/purchaseTokens', {
        method: 'POST',
        body: JSON.stringify({ packageId: selectedPkg.id }),
      });

      if (!intentRes.ok) {
        const err = await intentRes.json();
        throw new Error(err.error || 'Failed to create payment');
      }

      const { clientSecret, paymentIntentId } = await intentRes.json();

      // 2. Confirm payment with Stripe
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not available');
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        { payment_method: { card: cardElement } },
      );

      if (stripeError) {
        throw new Error(stripeError.message || 'Payment failed');
      }

      if (paymentIntent?.status !== 'succeeded') {
        throw new Error('Payment was not completed');
      }

      // 3. Confirm on backend to grant queries
      const confirmRes = await authFetch('/api/confirmTokenPurchase', {
        method: 'POST',
        body: JSON.stringify({ paymentIntentId }),
      });

      if (!confirmRes.ok) {
        const err = await confirmRes.json();
        throw new Error(err.error || 'Failed to confirm purchase');
      }

      const { queriesAdded } = await confirmRes.json();
      onSuccess(queriesAdded);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Purchase failed';
      setError(msg);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="token-modal-overlay" onClick={onClose}>
      <div className="token-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="token-modal-header">
          <h2>
            {t('tokens.buyMoreTitle')}
          </h2>
          <button className="token-modal-close" onClick={onClose} type="button">×</button>
        </div>

        {step === 'select' && (
          <div className="token-packages">
            <p className="token-packages-desc">
              {t('tokens.packageDescription')}
            </p>
            {PACKAGES.map(pkg => (
              <button
                key={pkg.id}
                className="token-package-card"
                onClick={() => handleSelectPackage(pkg)}
                type="button"
              >
                <span className="token-package-queries">{pkg.queries}</span>
                <span className="token-package-label">
                  {t('tokens.queries')}
                </span>
                <span className="token-package-price">{formatPrice(pkg.priceUsd)}</span>
                <span className="token-package-per">
                  {formatPrice(Math.round(pkg.priceUsd / pkg.queries))}/{t('tokens.query')}
                </span>
              </button>
            ))}
          </div>
        )}

        {step === 'pay' && selectedPkg && (
          <div className="token-payment">
            <div className="token-payment-summary">
              <span>{selectedPkg.queries} {t('tokens.queries')}</span>
              <strong>{formatPrice(selectedPkg.priceUsd)}</strong>
            </div>

            {isStripeTestMode && (
              <div className="token-test-banner">
                🧪 {t('tokens.testModeBanner')}
              </div>
            )}

            <div className="token-card-element">
              <CardElement
                options={{
                  style: {
                    base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } },
                    invalid: { color: '#9e2146' },
                  },
                }}
              />
            </div>

            {error && <div className="token-error">{error}</div>}

            <div className="token-payment-actions">
              <button className="token-btn-back" onClick={handleBack} disabled={processing} type="button">
                {t('tokens.back')}
              </button>
              <button
                className="token-btn-pay"
                onClick={handlePurchase}
                disabled={!stripe || processing}
                type="button"
              >
                {processing
                  ? t('tokens.processing')
                  : t('tokens.pay', { price: formatPrice(selectedPkg.priceUsd) })}
              </button>
            </div>

            <p className="token-secure-notice">
              🔒 {t('tokens.secureNotice')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Outer wrapper with Stripe Elements provider
// ---------------------------------------------------------------------------

const TokenPurchaseModal: React.FC<TokenPurchaseModalProps> = (props) => (
  <Elements stripe={getStripePromise()}>
    <TokenPurchaseForm {...props} />
  </Elements>
);

export default TokenPurchaseModal;
